import { pickAt } from './picking';
import { ToolBase, ToolPointer, ShiftLock } from './tool';
import { Vec3, vec, add, AXIS_Z, dominantAxis } from '../core/vec';
import { computeMoveDelta, SnapResult } from '../inference/inference';
import { cloneEntities, eraseEdges } from '../core/ops';
import { SNAP_COLORS } from '../render/preview';
import { formatLength, parseMeasureList } from '../core/units';

const CORNER_GRAB_RADIUS_PX = 24;

const SNAP_LABELS: Partial<Record<SnapResult['kind'], string>> = {
  endpoint: 'Eckpunkt',
  midpoint: 'Mittelpunkt',
  origin: 'Ursprung',
  grid: 'Raster',
  onEdge: 'Kante',
  onGuide: 'Hilfslinie'
};

/** Verschieben (M): Auswahl oder Element unter dem Cursor verschieben; ⌘/Strg = Kopie.
 *  Ecken per Hover anwählen → die nächstgelegene Objektecke rastet exakt an
 *  Eck-/Mittelpunkten anderer Shapes, am Ursprung und am Bodengitter ein. */
export class MoveTool extends ToolBase {
  readonly id = 'move';
  readonly label = 'Verschieben';
  readonly shortcut = 'M';
  readonly cursor = 'move';
  readonly hint = 'Element klicken und ziehen · Ecke anfahren = exakt fassen · Raster-/Eckpunkt-Fang · ⌘/Strg = Kopie · ←/→/↑ = Achse sperren (↑ = Höhe/Z) · Pfeiltaste ↓ lösen · Zahl tippen: dx;dy;dz';

  private dragging = false;
  private vertexIds: number[] = [];
  private originals = new Map<number, Vec3>();
  private ref: Vec3 = vec();
  /** Nächstgelegene Ecke des Objekts beim Fassen (Originalposition) — Ziel für Eckpunkt-Fang. */
  private handle: Vec3 = vec();
  /** Griff lag nahe einer Objektecke → Raster-Fang richtet die Ecke aus. */
  private cornerGrab = false;
  private plane: { point: Vec3; normal: Vec3 } | null = null;
  private lockedAxis: 0 | 1 | 2 | null = null;
  private copyMode = false;
  /** Beim Strg-Kopieren angelegte Kopie + vorherige Auswahl (für Abbruch/Klick ohne Ziehen). */
  private copy: { edges: Set<number>; faces: Set<number>; vertices: number[]; prior: { edges: Set<number>; faces: Set<number> } } | null = null;
  private downAt = { x: 0, y: 0 };
  private moved = false;
  private editBegun = false;
  /** Umschalt gehalten → Achse der aktuellen Bewegungsrichtung bleibt gesperrt. */
  private shiftLock = new ShiftLock<0 | 1 | 2>();
  private lastDelta: Vec3 = vec();

  enter(): void {
    this.dragging = false;
    this.lockedAxis = null;
    this.ctx.preview.clear();
  }

  exit(): void {
    if (this.dragging) this.restore();
    this.endDrag();
  }

  private restore(): void {
    for (const [id, p] of this.originals) {
      const v = this.ctx.model.vertices.get(id);
      if (v) v.p = p;
    }
    this.originals.clear();
    this.discardCopy();
  }

  /** Entfernt eine noch nicht platzierte Kopie wieder und stellt die vorherige Auswahl her. */
  private discardCopy(): void {
    const c = this.copy;
    this.copy = null;
    if (!c) return;
    for (const id of c.faces) this.ctx.model.deleteFace(id);
    eraseEdges(this.ctx.model, c.edges);
    for (const id of c.vertices) if (this.ctx.model.vertices.has(id)) this.ctx.model.pruneVertex(id);
    this.ctx.selection.restore(c.prior);
  }

  /** Zug-Zustand zurücksetzen (Achssperren gelten pro Zug, wie SketchUp-Inferenz). */
  private endDrag(): void {
    this.dragging = false;
    this.lockedAxis = null;
    this.shiftLock.reset();
    this.lastDelta = vec();
    this.originals.clear();
    this.plane = null;
    this.copy = null;
    this.moved = false;
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    // Klick – Bewegen – Klick (wie SketchUp): zweiter Klick setzt das Objekt ab
    if (this.dragging) {
      this.endDrag();
      return;
    }
    const sel = this.ctx.selection;
    // Zielbestimmung: Auswahl oder Element unter dem Cursor
    let edgeIds = new Set(sel.edges);
    let faceIds = new Set(sel.faces);
    if (sel.isEmpty) {
      edgeIds = new Set<number>();
      faceIds = new Set<number>();
      const { edgeId, faceId } = pickAt(this.ctx, e);
      // Element einer geschlossenen Gruppe → ganze Gruppe verschieben
      const target = this.ctx.model.expandToGroups(edgeId !== null ? [edgeId] : [], faceId !== null ? [faceId] : []);
      edgeIds = target.edges;
      faceIds = target.faces;
      if (edgeIds.size === 0 && faceIds.size === 0) return;
    }

    this.copyMode = e.ctrl;
    this.downAt = { x: e.x, y: e.y };
    this.moved = false;
    if (this.copyMode) {
      this.ctx.beginEdit();
      const prior = sel.snapshot();
      const cloned = cloneEntities(this.ctx.model, edgeIds, faceIds);
      edgeIds = cloned.edges;
      faceIds = cloned.faces;
      sel.set(edgeIds, faceIds);
      this.copy = { edges: cloned.edges, faces: cloned.faces, vertices: [...this.ctx.model.verticesOf(cloned.edges, cloned.faces)], prior };
    }

    this.vertexIds = [...this.ctx.model.verticesOf(edgeIds, faceIds)];
    if (this.vertexIds.length === 0) return;
    // Ohne Kopie: Undo-Schritt erst bei der ersten echten Bewegung (Klick ohne Zug = kein leerer Schritt)
    this.editBegun = this.copyMode;
    this.originals.clear();
    for (const id of this.vertexIds) this.originals.set(id, this.ctx.model.vertexPosition(id));

    // Griff: nächstgelegene Ecke des Objekts zum Cursor — Ziel für Eck-/Raster-Fang
    let bestDist = Infinity;
    for (const id of this.vertexIds) {
      const d = this.ctx.camera.screenDistance(this.originals.get(id)!, e.x, e.y);
      if (d < bestDist) {
        bestDist = d;
        this.handle = this.originals.get(id)!;
      }
    }

    // Referenzpunkt + Bewegungsebene; ohne Fläche: horizontale Ebene auf Griffhöhe
    const snap = this.ctx.inference.snap(e.x, e.y, e.ray, { fallbackPlane: null });
    this.ref = snap.point;
    this.cornerGrab = bestDist <= CORNER_GRAB_RADIUS_PX;
    if (snap.faceId !== undefined && snap.plane) this.plane = snap.plane;
    else this.plane = { point: vec(0, 0, snap.point.z), normal: AXIS_Z };
    this.dragging = true;
    this.ctx.ui.setStatus('Ziehen zum Verschieben · nahe Ecke = Eckpunkt-Fang · ⌘/Strg = Kopie · Zahl tippen: dx;dy;dz');
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();

    // Ohne Drag: Snap-Punkte (v. a. Ecken) per Marker anwählbar machen
    if (!this.dragging || this.originals.size === 0) {
      const hover = this.ctx.inference.snap(e.x, e.y, e.ray, { fallbackPlane: null });
      this.showSnapDot(hover);
      this.ctx.requestUpdate();
      return;
    }

    if (Math.hypot(e.x - this.downAt.x, e.y - this.downAt.y) > 4) this.moved = true;
    // Eigenvertices ausschließen, Flächen-Snaps aus → kein Selbstfang, kein freies Driften.
    // Bei Achssperre folgt der Cursor stattdessen einer bildschirmparallelen Ebene,
    // damit z. B. die blaue Achse (↑) echte Z-Bewegung liefert.
    const axisLock = this.lockedAxis ?? this.shiftLock.update(e.shift, () => dominantAxis(this.lastDelta));
    const movePlane = axisLock !== null
      ? { point: this.ref, normal: this.ctx.camera.viewDirection() }
      : this.plane;
    const snap = this.ctx.inference.snap(e.x, e.y, e.ray, {
      fallbackPlane: movePlane,
      excludeVertices: new Set(this.vertexIds),
      includeFaces: false
    });
    const delta = computeMoveDelta(snap, this.ref, this.handle, movePlane, axisLock, this.cornerGrab);

    this.lastDelta = delta;
    if (!this.editBegun && (delta.x !== 0 || delta.y !== 0 || delta.z !== 0)) {
      this.ctx.beginEdit();
      this.editBegun = true;
    }
    for (const [id, p] of this.originals) {
      const v = this.ctx.model.vertices.get(id);
      if (v) v.p = add(p, delta);
    }
    this.showSnapDot(snap);
    const label = SNAP_LABELS[snap.kind];
    this.ctx.ui.setStatus(
      `Δ ${formatLength(delta.x, this.ctx.units())}, ${formatLength(delta.y, this.ctx.units())}, ${formatLength(delta.z, this.ctx.units())}` +
        (axisLock !== null ? ` · ${['rote', 'grüne', 'blaue'][axisLock]} Achse gesperrt${this.lockedAxis === null ? ' (Umschalt)' : ''}` : '') +
        (label ? ` · ${label}` : '') +
        (this.copyMode ? ' · Kopie' : '')
    );
    this.ctx.requestUpdate();
  }

  private showSnapDot(snap: SnapResult): void {
    if (snap.kind !== 'ground') {
      this.ctx.preview.dot(snap.point, SNAP_COLORS[snap.kind] ?? 0x333333, snap.priority >= 4 ? 11 : 8);
    }
  }

  pointerUp(_e: ToolPointer): void {
    if (!this.dragging) return;
    // Ohne Ziehen losgelassen → Objekt hängt am Cursor bis zum nächsten Klick; Zahl tippen geht auch
    if (!this.moved) {
      this.ctx.ui.setStatus('Maus bewegen und klicken zum Absetzen · Zahl tippen: dx;dy;dz · Esc bricht ab');
      return;
    }
    this.endDrag();
  }

  keyDown(e: KeyboardEvent): void {
    if (e.key === 'ArrowLeft') this.lockedAxis = 1;
    else if (e.key === 'ArrowRight') this.lockedAxis = 0;
    else if (e.key === 'ArrowUp') this.lockedAxis = 2;
    else if (e.key === 'ArrowDown') this.lockedAxis = null;
  }

  wantsVcb(): boolean {
    return this.dragging;
  }

  vcbDisplay(): string | null {
    return null;
  }

  applyVcb(raw: string): void {
    if (!this.dragging || this.originals.size === 0) return;
    const values = parseMeasureList(raw, 'length', this.ctx.units());
    if (!values || values.length === 0) return;
    const delta = vec(values[0] ?? 0, values[1] ?? 0, values[2] ?? 0);
    if (!this.editBegun) {
      // Snapshot mit Ausgangslage (Vorschau zurücksetzen, dann sichern)
      for (const [id, p] of this.originals) {
        const v = this.ctx.model.vertices.get(id);
        if (v) v.p = p;
      }
      this.ctx.beginEdit();
      this.editBegun = true;
    }
    for (const [id, p] of this.originals) {
      const v = this.ctx.model.vertices.get(id);
      if (v) v.p = add(p, delta);
    }
    this.endDrag();
  }

  cancel(): void {
    if (this.dragging) this.restore();
    this.endDrag();
  }
}

